/**
 * 用户状态管理 Store
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserInfo, PermissionInfo } from '@/services/auth';
import { authVerifyAdmin } from '@/services/auth';

interface UserState {
  // 用户信息
  userInfo: UserInfo | null;
  // 用户权限列表
  permissions: PermissionInfo[];
  // 是否已登录
  isLoggedIn: boolean;
  // 是否为本产品管理员（来自统一鉴权中心 verify-admin）
  isAdmin: boolean;

  // Actions
  setUserInfo: (userInfo: UserInfo | null) => void;
  setPermissions: (permissions: PermissionInfo[]) => void;
  setIsAdmin: (isAdmin: boolean) => void;
  login: (userInfo: UserInfo, permissions?: PermissionInfo[]) => void;
  logout: () => void;
  hasPermission: (permissionId: string) => boolean;
  /** 主动刷新管理员身份（成功返回最新值，失败返回 false） */
  refreshIsAdmin: () => Promise<boolean>;
}

const PRODUCT_ID = import.meta.env.VITE_AUTH_PRODUCT_ID || 'P00006';

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      userInfo: null,
      permissions: [],
      isLoggedIn: false,
      isAdmin: false,

      setUserInfo: (userInfo) => {
        set({ userInfo, isLoggedIn: !!userInfo });
      },

      setPermissions: (permissions) => {
        set({ permissions });
      },

      setIsAdmin: (isAdmin) => {
        set({ isAdmin });
      },

      login: (userInfo, permissions = []) => {
        set({
          userInfo,
          permissions,
          isLoggedIn: true,
        });
      },

      logout: () => {
        set({
          userInfo: null,
          permissions: [],
          isLoggedIn: false,
          isAdmin: false,
        });
        // 清除 localStorage
        localStorage.removeItem('user-storage');
      },

      hasPermission: (permissionId: string) => {
        const { permissions } = get();
        if (!Array.isArray(permissions)) return false;
        return permissions.some(
          (p) => p.id === permissionId && p.productId === PRODUCT_ID
        );
      },

      refreshIsAdmin: async () => {
        const { userInfo } = get();
        if (!userInfo?.id) {
          set({ isAdmin: false });
          return false;
        }
        try {
          const resp = await authVerifyAdmin({
            userId: userInfo.id,
            productId: PRODUCT_ID,
          });
          const isAdmin = resp.data?.isAdmin === true;
          set({ isAdmin });
          return isAdmin;
        } catch {
          set({ isAdmin: false });
          return false;
        }
      },
    }),
    {
      name: 'user-storage', // localStorage key
      partialize: (state) => ({
        userInfo: state.userInfo,
        permissions: state.permissions,
        isLoggedIn: state.isLoggedIn,
        isAdmin: state.isAdmin,
      }),
      merge: (persistedState: any, currentState) => ({
        ...currentState,
        ...persistedState,
        permissions: Array.isArray(persistedState?.permissions)
          ? persistedState.permissions
          : [],
      }),
    }
  )
);
